// ===========================================
// COMPONENTE: InvitadosReunion
// ===========================================
// Invitados de una reunión con la respuesta de cada uno según Google Calendar (ORR-544)

import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useInvitadosReunion } from '@/core/hooks/useCalendario'
import type { EstadoInvitado, EventoCalendario } from '@/core/types/calendario'

const ESTADO_CONFIG: Record<EstadoInvitado, { label: string; className: string }> = {
  aceptado: { label: 'Aceptó', className: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30' },
  rechazado: { label: 'Rechazó', className: 'text-red-600 bg-red-50 dark:bg-red-950/30' },
  tal_vez: { label: 'Tal vez', className: 'text-amber-600 bg-amber-50 dark:bg-amber-950/30' },
  sin_respuesta: { label: 'Sin respuesta', className: 'text-muted-foreground bg-muted' },
}

const normalizar = (email: string) => email.trim().toLowerCase()

interface InvitadoVisible {
  email: string
  estado: EstadoInvitado | null
  faltaEnGoogle: boolean
}

/**
 * Lista los invitados de BrainHub y los que solo están en Google.
 * Sin lectura de Google, cada invitado se muestra sin estado.
 */
export const InvitadosReunion = ({ evento }: { evento: EventoCalendario }) => {
  const { query, habilitado } = useInvitadosReunion(evento)
  const participantes = evento.participantes ?? []
  const lectura = query.data?.enGoogle ? query.data : null

  const estadoPorEmail = new Map(lectura?.invitados.map((i) => [normalizar(i.email), i.estado] as const))
  const faltan = new Set(lectura?.faltanEnGoogle.map(normalizar))
  const enBrainHub = new Set(participantes.map(normalizar))

  const invitados: InvitadoVisible[] = [
    ...participantes.map((email) => ({
      email,
      estado: estadoPorEmail.get(normalizar(email)) ?? null,
      faltaEnGoogle: faltan.has(normalizar(email)),
    })),
    ...(lectura?.invitados ?? [])
      .filter((i) => !enBrainHub.has(normalizar(i.email)))
      .map((i) => ({ email: i.email, estado: i.estado, faltaEnGoogle: false })),
  ]

  // Sin invitados, el bloque se muestra solo mientras lee Google, si la lectura falla o si Google no tiene la reunión.
  if (invitados.length === 0 && (!habilitado || (query.data?.enGoogle ?? false))) return null

  const mensaje = query.isError
    ? 'No se pudo leer el estado en Google'
    : query.data?.enGoogle === false
      ? 'La reunión no está en Google Calendar'
      : query.data
        ? `Respuestas según Google Calendar, leídas a las ${format(new Date(query.data.leidoAt), 'HH:mm')}`
        : null

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">Invitados</p>
      <div className="flex flex-wrap gap-1.5">
        {invitados.map(({ email, estado, faltaEnGoogle }) => (
          <Badge key={email} variant="secondary" className="font-normal gap-1.5">
            {email}
            {estado && (
              <span className={cn('rounded px-1.5 text-[11px] font-medium', ESTADO_CONFIG[estado].className)}>
                {ESTADO_CONFIG[estado].label}
              </span>
            )}
            {faltaEnGoogle && (
              <span className="inline-flex items-center gap-1 rounded px-1.5 text-[11px] font-medium text-amber-600 bg-amber-50 dark:bg-amber-950/30">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                No figura en Google
              </span>
            )}
          </Badge>
        ))}
      </div>

      {habilitado && (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          {query.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              <span>Leyendo las respuestas en Google…</span>
            </>
          ) : (
            <>
              <span>{mensaje}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs"
                onClick={() => query.refetch()}
                disabled={query.isFetching}
                aria-label="Volver a leer las respuestas en Google"
              >
                <RefreshCw className={cn('h-3 w-3', query.isFetching && 'animate-spin')} aria-hidden="true" />
                <span className="ml-1">{query.isError ? 'Reintentar' : 'Volver a leer'}</span>
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
